/**
 * Sync event schema - the vocabulary of messages exchanged between
 * the Host Control Panel and the Projector View.
 *
 * Every event carries { type, pluginId, payload, ts }.
 * The Bingo plugin listens only to events where pluginId === "bingo".
 * Future plugins define their own event types under their own pluginId.
 */

export const CORE_EVENTS = {
  // Cross-plugin, sent by the Core
  HOTEL_BRANDING_UPDATED: "core/hotel-branding-updated",
  SETTINGS_UPDATED: "core/settings-updated",
  THEME_UPDATED: "core/theme-updated",
  MUTE_TOGGLED: "core/mute-toggled",
};

export const BINGO_EVENTS = {
  NEW_GAME: "bingo/new-game",
  NUMBER_CALLED: "bingo/number-called",
  NUMBER_UNDONE: "bingo/number-undone",
  NUMBER_REDONE: "bingo/number-redone",
  MODE_CHANGED: "bingo/mode-changed",
  INTERVAL_CHANGED: "bingo/interval-changed",
  PAUSED: "bingo/paused",
  RESUMED: "bingo/resumed",
  STAGE_CHANGED: "bingo/stage-changed",
  WINNER_ANNOUNCED: "bingo/winner-announced",
  WINNER_DISMISSED: "bingo/winner-dismissed",
  MANUAL_MARK: "bingo/manual-mark",
  MANUAL_UNMARK: "bingo/manual-unmark",
  END_GAME: "bingo/end-game",
  GAME_SUMMARY_DISMISSED: "bingo/game-summary-dismissed",
  RESUME_SESSION: "bingo/resume-session",

  // Cross-window handshake so a late-joining Projector can hydrate
  // its state from the Host.
  STATE_REQUEST: "bingo/state-request",
  STATE_SNAPSHOT: "bingo/state-snapshot",

  // Bingo plugin settings updated on the Host — Projector re-reads.
  SETTINGS_UPDATED: "bingo/settings-updated",

  // Rehearsal helper: play a sample standard reveal on all open windows
  // WITHOUT touching the real game. Payload: { value, ts }.
  PREVIEW_REVEAL: "bingo/preview-reveal",

  // ==================== Bingo Check · Heartbeat Verification ====================
  // A calm, cinematic winner-verification mode. Payloads below.
  //
  // BINGO_CHECK_STARTED   { numbers: number[], playerLabel?: string }
  //   Host is initiating verification of the claimed line. Projector
  //   opens the heartbeat overlay and starts the audio loop.
  //
  // BINGO_CHECK_VERIFIED  { verifiedCount: number, bpm: number }
  //   Host has confirmed one more number. Projector plays confirm
  //   chime and green-ticks up to `verifiedCount`. `bpm` is the
  //   new heartbeat cadence for the visual pulse.
  //
  // BINGO_CHECK_CANCELLED { }
  //   Host aborted. Projector closes the overlay and stops audio.
  //
  // BINGO_CHECK_COMPLETED { }
  //   The final number has just been confirmed. Projector stops the
  //   heartbeat instantly. The Confirmed Bingo Celebration module
  //   (next in Phase 2) picks up from here.
  BINGO_CHECK_STARTED: "bingo/bingo-check-started",
  BINGO_CHECK_VERIFIED: "bingo/bingo-check-verified",
  BINGO_CHECK_CANCELLED: "bingo/bingo-check-cancelled",
  BINGO_CHECK_COMPLETED: "bingo/bingo-check-completed",

  // ==================== Confirmed Bingo Celebration ====================
  // CONFIRMED_BINGO_STARTED   { name, stageLabel, ts }
  //   Fired right after BINGO_CHECK_COMPLETED once the silence beat
  //   finishes. Projector opens the hybrid celebration overlay.
  //
  // CONFIRMED_BINGO_DISMISSED { }
  //   Host clicked Continue (or auto-return fired). Projector closes.
  CONFIRMED_BINGO_STARTED: "bingo/confirmed-bingo-started",
  CONFIRMED_BINGO_DISMISSED: "bingo/confirmed-bingo-dismissed",
};

export const makeEvent = (type, payload = {}, pluginId = "core") => ({
  type,
  pluginId,
  payload,
  ts: Date.now(),
});
